import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import {
    Typography, ListItemAvatar, Avatar, Grid, ListItem, ListItemText, ListItemSecondaryAction,
    Box, MenuItem, Button, FormControl, Input, InputAdornment, TextField, LinearProgress
} from '@material-ui/core';
import Select from '@mui/material/Select';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { HiUserGroup } from 'react-icons/hi';
import { FaCheck } from 'react-icons/fa';
import { BsSearch, BsDot, BsFilter } from 'react-icons/bs';
import { BiSortDown, BiSortUp } from 'react-icons/bi';
import { MdClear } from 'react-icons/md';
import { debounce } from 'lodash'; // Add lodash for debouncing
import { PREFETCH_ROWS, useServerPagedGroups } from '../../../hooks/useServerPagedGroups';
import ProgressiveTags from './ProgressiveTags';
import { DefaultGroupSort, sortGroupsByUpdateDate } from '../../../helpers/Utils/groupSortUtils';

const ITEM_HEIGHT = 54; // Height of each list item in pixels

const Groups = ({
    classes,
    list,
    bsDot,
    isSms,
    selectedList,
    innerHeight,
    isNotifications,
    noSelectionText,
    showSortBy = true,
    showFilter = true,
    isCampaign = false,
    showSelectAll = true,
    callbackSelectedGroups,
    callbackUpdateGroups,
    callbackSelectAll,
    callbackReciFilter,
    callbackShowTestGroup,
    uniqueKey,
    groupCompareKey = '',
    // PR-3790: fetchPage turns on server-side paging; omit it for the old in-memory
    // behaviour. The defaults matter - without them TS infers these as required props.
    fetchPage = /** @type {undefined | ((params: any) => Promise<{groups: any[], recordCount: number}>)} */ (undefined),
    // Rows that come from a different endpoint and should stay above the paged
    // results (test groups).
    pinnedRows = /** @type {undefined | any[]} */ (undefined),
    // Bump to force a re-fetch from page 1, e.g. after a group is created.
    reloadToken = 0,
    // Total matching rows, when the caller already knows it.
    totalCount = /** @type {undefined | number} */ (undefined)
}) => {
    const { windowSize, isRTL } = useSelector(state => state.core);
    const { t } = useTranslation();

    const [groupNameSearch, setGroupNameSearch] = useState('');
    const [clearInput, setClearInput] = useState(false);
    const [groupHover, setIsHover] = useState(null);
    const [showTestGroups, setShowTestGroups] = useState(false);
    const [sortBySelected, setSortBy] = useState(DefaultGroupSort.FIELD);
    const [sortDirection, setSortDirection] = useState(DefaultGroupSort.DIRECTION);

    // Debounced search to prevent excessive filtering
    const debouncedSearch = useCallback(
        debounce((searchTerm) => {
            setGroupNameSearch(searchTerm);
        }, 300),
        []
    );

    // PR-3790: paging is opt-in. Without `fetchPage` this hook is inert.
    const paged = useServerPagedGroups({
        fetchPage,
        // Test groups are never part of the paged result: they come from their
        // own endpoint and are supplied as pinnedRows. Asking the server for
        // them too would list every test group twice.
        showTestGroups: false,
        sortBySelected,
        sortDirection,
        reloadToken
    });

    const handleSearch = (event) => {
        const value = event.target.value;
        setClearInput(value !== '');
        if (paged.enabled) {
            // Search runs on the server; the hook owns its own debounce.
            paged.onSearchChange(value);
            return;
        }
        debouncedSearch(value);
    };

    // Memoized filtered and sorted list
    const processedList = useMemo(() => {
        if (!list || list.length === 0) return [];

        let tempList = [...list];

        // Apply search filter
        if (groupNameSearch.trim()) {
            const searchTerm = groupNameSearch.trim().toLowerCase();
            tempList = tempList.filter((item) => {
                const name = isCampaign ? item.Name : item.GroupName;
                return name.toLowerCase().includes(searchTerm);
            });
        }

        // Apply sorting
        if (sortBySelected === "Group Name" && !isCampaign) {
            tempList.sort((a, b) => {
                const aName = a.GroupName.trim().toUpperCase();
                const bName = b.GroupName.trim().toUpperCase();
                return sortDirection === 'asc'
                    ? aName.localeCompare(bName)
                    : bName.localeCompare(aName);
            });
        } else if (sortBySelected === "Update Date") {
            const idKey = groupCompareKey === '' ? (isNotifications ? "Id" : "GroupID") : groupCompareKey;
            tempList = sortGroupsByUpdateDate(tempList, sortDirection, idKey);
        } else if (sortBySelected === "Creation Date") {
            tempList.sort((a, b) => {
                const dateKey = isSms ? 'CreationDate' : 'CreatedDate';
                const aDate = a[dateKey] ? Date.parse(a[dateKey]) : 0;
                const bDate = b[dateKey] ? Date.parse(b[dateKey]) : 0;
                return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
            });
        }

        return tempList;
    }, [list, groupNameSearch, sortBySelected, sortDirection, isCampaign, isSms, isNotifications, groupCompareKey]);

    const resetSearch = useCallback(() => {
        // PR-3790: guard the DOM lookup - two pickers can be on one page, so the
        // hardcoded id is not guaranteed to resolve to this component's input.
        const input = document.querySelector('#searchGroup');
        if (input) input.value = '';
        setGroupNameSearch('');
        setClearInput(false);
        debouncedSearch.cancel();
        paged.resetSearch();
    }, [debouncedSearch, paged]);

    // PR-3790: in paged mode the rows come from the server. Rows from other
    // endpoints (test groups) are pinned above them.
    const displayList = useMemo(() => {
        if (!paged.enabled) return processedList;
        const pinned = pinnedRows || [];
        return pinned.length > 0 ? pinned.concat(paged.rows) : paged.rows;
    }, [paged.enabled, paged.rows, pinnedRows, processedList]);

    const showLoadingRow = paged.enabled && paged.isLoading;

    // Ensure selectedList is always an array to prevent reduce errors
    const safeSelectedList = Array.isArray(selectedList) ? selectedList : [];

    // PR-3790: identity keys, matching the ones the item renderers use. The
    // Autocomplete previously compared selected entries by NAME, so two groups
    // sharing a name collided.
    const groupIdKey = groupCompareKey === '' ? (isNotifications ? "Id" : "GroupID") : groupCompareKey;
    const campaignIdKey = isSms ? 'SMSCampaignID' : 'CampaignID';

    const onSelectGroup = useCallback((group) => {
        callbackSelectedGroups(group);
    }, [callbackSelectedGroups]);

    const onTagChange = useCallback((event, value) => {
        // Ensure value is an array before passing to callback
        const safeValue = Array.isArray(value) ? value : [];
        callbackUpdateGroups(safeValue, event);
    }, [callbackUpdateGroups]);

    // Virtual list item renderer
    const ListItemRenderer = useCallback(({ index, style }) => {
        if (index === 0 && showSelectAll) {
            // Render "Select All" item
            // PR-3790: in paged mode the loaded rows are only one page, so the
            // total comes from the server instead.
            const totalMatching = paged.enabled
                ? (typeof totalCount === 'number' ? totalCount : paged.recordCount) + (pinnedRows?.length || 0)
                : processedList.length;
            const allSelected = totalMatching > 0 && totalMatching === safeSelectedList.length;

            return (
                <div style={style}>
                    <ListItem
                        onClick={() => callbackSelectAll(paged.enabled ? paged.filter : undefined)}
                        style={{ cursor: 'pointer', height: ITEM_HEIGHT }}
                        className="group-container"
                    >
                        <ListItemAvatar>
                            <Avatar className={clsx(
                                classes.listIcon,
                                allSelected ? classes.redBg : classes.transparentBg,
                                allSelected ? classes.white : classes.blue,
                                allSelected ? classes.borderRed : classes.borderPrimary
                            )}>
                                {allSelected ?
                                    <FaCheck className={classes.white} /> :
                                    <HiUserGroup className={classes.colrPrimary} />
                                }
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            className={clsx('groupText', !isRTL && classes.textLeft)}
                            title={t("notifications.selectAll")}
                            primary={t("notifications.selectAll")}
                        />
                    </ListItem>
                </div>
            );
        }

        const actualIndex = showSelectAll ? index - 1 : index;
        const item = displayList[actualIndex];

        if (!item) {
            // Trailing slot while the next page is in flight.
            return <div style={style}>{showLoadingRow && <Box width="100%" paddingTop="24px"><LinearProgress /></Box>}</div>;
        }

        if (isCampaign) {
            return <CampaignItemRenderer
                style={style}
                item={item}
                selectedList={safeSelectedList}
                isSms={isSms}
                onSelectGroup={onSelectGroup}
                classes={classes}
                isRTL={isRTL}
                groupHover={groupHover}
                setIsHover={setIsHover}
            />;
        } else {
            return <GroupItemRenderer
                style={style}
                item={item}
                selectedList={safeSelectedList}
                onSelectGroup={onSelectGroup}
                classes={classes}
                isRTL={isRTL}
                groupHover={groupHover}
                setIsHover={setIsHover}
                isNotifications={isNotifications}
                groupCompareKey={groupCompareKey}
                t={t}
            />;
        }
    }, [displayList, processedList, paged.enabled, paged.recordCount, paged.filter, totalCount, pinnedRows, showLoadingRow, safeSelectedList, showSelectAll, isCampaign, isSms, onSelectGroup, classes, isRTL, groupHover, isNotifications, groupCompareKey, t, callbackSelectAll]);

    const totalItems = displayList.length + (showSelectAll ? 1 : 0) + (showLoadingRow ? 1 : 0);

    // PR-3790: load the next page once the last loaded row scrolls into view.
    // Row 0 is "Select All" when shown, so the last group row sits one further down.
    const handleItemsRendered = useCallback(({ visibleStopIndex }) => {
        if (!paged.enabled || !paged.hasMore || paged.isLoading) return;
        const lastRowIndex = displayList.length - 1 + (showSelectAll ? 1 : 0);
        if (visibleStopIndex >= lastRowIndex - PREFETCH_ROWS) {
            paged.loadMore();
        }
    }, [paged, displayList.length, showSelectAll]);

    const groupSortOptions = [
        { value: "Group Name", text: t("notifications.sort_by_group") },
        { value: "Creation Date", text: t("notifications.sort_by_creation") },
        { value: "Update Date", text: t("notifications.sort_by_updated") },
    ];

    const handleSortBySelected = (event) => {
        setSortBy(event.target.value);
    };

    const handleSortDirection = () => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    return (
        <Box className={classes.groupsContainer} key={uniqueKey}>
            {/* Search and Filter Controls */}
            {windowSize === 'xs' && (
                <Grid item xs={12}>
                    <FormControl className={clsx(classes.margin, classes.searchInput)}>
                        <Input
                            autoComplete='off'
                            onChange={handleSearch}
                            placeholder={t('notifications.buttons.search')}
                            id="searchGroup"
                            startAdornment={
                                <InputAdornment position="start">
                                    <BsSearch />
                                </InputAdornment>
                            }
                            endAdornment={clearInput && (
                                <InputAdornment position="start" onClick={resetSearch}>
                                    <MdClear style={{ cursor: 'pointer' }} />
                                </InputAdornment>
                            )}
                        />
                    </FormControl>
                </Grid>
            )}

            <Grid item xs={12} className={clsx(classes.flex, classes.groupFilterRow)}>
                {windowSize !== 'xs' && (
                    <FormControl className={clsx(classes.margin, classes.searchInput)}>
                        <Input
                            autoComplete='off'
                            onChange={handleSearch}
                            placeholder={t('notifications.buttons.search')}
                            id="searchGroup"
                            startAdornment={
                                <InputAdornment position="start">
                                    <BsSearch />
                                </InputAdornment>
                            }
                            endAdornment={clearInput && (
                                <InputAdornment position="start" onClick={resetSearch}>
                                    <MdClear style={{ cursor: 'pointer' }} />
                                </InputAdornment>
                            )}
                        />
                    </FormControl>
                )}

                {showSortBy && (
                    <Box className={classes.filterButtonsContainer}>
                        {safeSelectedList.length > 0 && showFilter && (
                            <Button
                                className={clsx(classes.formControl, classes.dropDown, classes.mt1)}
                                onClick={callbackReciFilter}
                                style={{ height: "36px", fontWeight: "600", textTransform: "capitalize" }}
                            >
                                {windowSize !== 'xs' && <BsFilter style={{ fontSize: "22px", color: "#ff3343" }} />}
                                {bsDot && <BsDot style={{ position: "absolute", left: "8px", top: "-6px", fontSize: "28px" }} />}
                                {t("mainReport.recipientFilter")}
                            </Button>
                        )}

                        {isSms && (
                            <Button
                                variant="outlined"
                                className={clsx(classes.formControl, classes.ml5, classes.mt1, showTestGroups ? classes.buttonActiveRed : classes.twoLineButton)}
                                onClick={() => {
                                    callbackShowTestGroup && callbackShowTestGroup(showTestGroups);
                                    setShowTestGroups(!showTestGroups);
                                }}
                            >
                                {t("sms.showTestGroups")}
                            </Button>
                        )}

                        <FormControl className={clsx(classes.dropDown, classes.mt1)}>
                            <Select
                                variant="standard"
                                value={sortBySelected}
                                onChange={handleSortBySelected}
                                className={clsx(classes.paddingSides10)}
                                style={{
                                    color: 'inherit',
                                    fontFamily: 'Assistant',
                                    lineHeight: 1.3
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                            direction: isRTL ? 'rtl' : 'ltr'
                                        },
                                    },
                                }}
                            >
                                {groupSortOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.text}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            className={clsx(classes.mt1, classes.formControl, classes.dropDown)}
                            onClick={handleSortDirection}
                            style={{ margin: safeSelectedList.length > 0 && showFilter && windowSize === 'xs' ? '5px 0px' : null }}
                        >
                            {sortDirection === 'asc' ? <BiSortDown /> : <BiSortUp />}
                        </Button>
                    </Box>
                )}
            </Grid>

            {/* Selected Groups Display */}
            {isCampaign ? (
                <Autocomplete
                    multiple
                    value={safeSelectedList}
                    options={safeSelectedList}
                    getOptionSelected={(option, value) => option[campaignIdKey] === value[campaignIdKey]}
                    getOptionLabel={(campaign) => campaign.Name || ''}
                    renderTags={(value, getTagProps) => (
                        <ProgressiveTags value={value} getTagProps={getTagProps} getLabel={(campaign) => campaign.Name || ''} />
                    )}
                    defaultValue={[]}
                    open={false}
                    popupIcon={false}
                    onChange={onTagChange}
                    renderInput={(params) => safeSelectedList.length > 0 ? (
                        <TextField {...params} className={clsx(classes.bottomShadow, classes.tagSelected, classes.sidebar)} style={{ maxHeight: 45 }} />
                    ) : (
                        <Typography className={clsx(classes.bottomShadow, classes.noSelection)}>
                            {noSelectionText || t('notifications.noGroupsSelected')}
                        </Typography>
                    )}
                />
            ) : (
                <Autocomplete
                    multiple
                    value={safeSelectedList}
                    options={safeSelectedList}
                    getOptionSelected={(option, value) => option[groupIdKey] === value[groupIdKey]}
                    getOptionLabel={(group) => group.GroupName || ''}
                    renderTags={(value, getTagProps) => (
                        <ProgressiveTags value={value} getTagProps={getTagProps} getLabel={(group) => group.GroupName || ''} />
                    )}
                    defaultValue={[]}
                    open={false}
                    popupIcon={false}
                    onChange={onTagChange}
                    renderInput={(params) => safeSelectedList.length > 0 ? (
                        <TextField {...params} className={clsx(classes.bottomShadow, classes.tagSelected, classes.sidebar)} style={{ maxHeight: 45 }} />
                    ) : (
                        <Typography className={clsx(classes.bottomShadow, classes.noSelection)}>
                            {noSelectionText || t('notifications.noGroupsSelected')}
                        </Typography>
                    )}
                />
            )}

            {/* Virtual List */}
            <div className={clsx(classes.demo, classes.sidebar)} style={{ height: innerHeight }}>
                <List
                    style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                    height={innerHeight}
                    itemCount={totalItems}
                    itemSize={ITEM_HEIGHT}
                    overscanCount={5} // Render a few extra items for smooth scrolling
                    onItemsRendered={handleItemsRendered}
                >
                    {ListItemRenderer}
                </List>
            </div>
        </Box>
    );
};

// Separate components for better performance
const GroupItemRenderer = React.memo(({
    style, item, selectedList, onSelectGroup, classes, isRTL,
    groupHover, setIsHover, isNotifications, groupCompareKey, t
}) => {
    const groupIdKey = groupCompareKey === '' ? (isNotifications ? "Id" : "GroupID") : groupCompareKey;
    const groupRecipientsKey = isNotifications ? "Members" : "Recipients";
    const isExist = selectedList?.some(group => group[groupIdKey] === item[groupIdKey]);

    return (
        <div style={style}>
            <ListItem
                id={item[groupIdKey]}
                key={item[groupIdKey]}
                onClick={() => onSelectGroup(item)}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setIsHover(item[groupIdKey])}
                onMouseLeave={() => setIsHover(null)}
                className={clsx(groupHover === item[groupIdKey] ? classes.hoverListItem : null, 'group-container')}
            >
                <ListItemAvatar className={classes.itemAvatar}>
                    <Avatar className={clsx(
                        classes.listIcon,
                        isExist ? classes.redBg : classes.transparentBg,
                        isExist ? classes.white : classes.blue,
                        isExist ? classes.borderRed : classes.borderPrimary
                    )}>
                        {isExist ?
                            <FaCheck className={clsx(classes.white)} /> :
                            <HiUserGroup className={clsx(classes.colrPrimary)} />
                        }
                    </Avatar>
                </ListItemAvatar>
                <Box dir={isRTL ? "rtl" : "ltr"} width="100%">
                    <ListItemText
                        className={clsx('groupText', !isRTL && classes.textLeft)}
                        title={item.GroupName}
                        primaryTypographyProps={{ variant: 'body1', style: { lineHeight: 1.5 } }}
                        primary={item.GroupName}
                    />
                </Box>
                <Box dir={isRTL ? "rtl" : "ltr"} width="100%">
                    <Box className={clsx('groupText', classes.itemAvatar, isRTL ? classes.textLeft : classes.textRight)}>
                        {item[groupRecipientsKey]?.toLocaleString()} {item[groupRecipientsKey] !== 1 ? t("notifications.recipients") : t("notifications.recipient")}
                    </Box>
                </Box>
            </ListItem>
        </div>
    );
});

const CampaignItemRenderer = React.memo(({
    style, item, selectedList, isSms, onSelectGroup, classes, isRTL, groupHover, setIsHover
}) => {
    const idKey = isSms ? 'SMSCampaignID' : 'CampaignID';
    const isExist = selectedList?.some(c => c[idKey] === item[idKey]);

    return (
        <div style={style}>
            <ListItem
                onClick={() => onSelectGroup(item)}
                style={{ cursor: 'pointer', height: ITEM_HEIGHT }}
                onMouseEnter={() => setIsHover(item[idKey])}
                onMouseLeave={() => setIsHover(null)}
                className={groupHover === item[idKey] ? classes.hoverListItem : null}
            >
                <ListItemAvatar>
                    <Avatar className={clsx(
                        classes.listIcon,
                        isExist ? classes.redBg : classes.transparentBg,
                        isExist ? classes.white : classes.blue,
                        isExist ? classes.borderRed : classes.borderPrimary
                    )}>
                        {isExist ?
                            <FaCheck className={classes.white} /> :
                            <HiUserGroup className={classes.colrPrimary} />
                        }
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    className={clsx('groupText', !isRTL && classes.textLeft)}
                    title={item.Name}
                    primary={item.Name}
                />
            </ListItem>
        </div>
    );
});

export default React.memo(Groups);